import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Youtube, ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { YOUTUBE_VIDEOS, YOUTUBE_URL } from "../data/content";

const VideoCard = ({ video, index }) => {
  const [playing, setPlaying] = useState(false);
  return (
    <Reveal delay={index * 0.1} className="h-full">
      <div
        data-testid={`youtube-video-${index + 1}`}
        className="group relative rounded-2xl overflow-hidden border border-[#E9DCC8] bg-[#FFFCF7] hover:border-[#B8893B]/50 transition-colors duration-500"
      >
        <div className="relative aspect-video">
          <AnimatePresence mode="wait">
            {playing ? (
              <motion.iframe
                key="player"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="absolute inset-0 w-full h-full"
                src={`https://www.youtube-nocookie.com/embed/${video.id}?autoplay=1&rel=0`}
                title={video.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <motion.button
                key="facade"
                exit={{ opacity: 0 }}
                onClick={() => setPlaying(true)}
                data-testid={`youtube-play-${index + 1}`}
                className="absolute inset-0 w-full h-full cursor-pointer"
                aria-label={`Play video: ${video.title}`}
              >
                <img
                  src={`https://i.ytimg.com/vi/${video.id}/hqdefault.jpg`}
                  alt={video.title}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-[1.2s] ease-out"
                />
                <span className="absolute inset-0 bg-[#2A1F16]/35 group-hover:bg-[#2A1F16]/20 transition-colors duration-500" aria-hidden="true" />
                <span className="absolute inset-0 flex items-center justify-center" aria-hidden="true">
                  <span className="h-14 w-14 rounded-full bg-[#C9A25F] text-[#2A1F16] flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-[0_10px_30px_-8px_rgba(184,137,59,0.6)]">
                    <Play size={20} className="ml-0.5" fill="currentColor" />
                  </span>
                </span>
              </motion.button>
            )}
          </AnimatePresence>
        </div>
        <div className="p-5 flex items-start justify-between gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#B8893B]">{video.tag}</p>
            <h3 className="mt-2 font-serif-ed text-lg sm:text-xl text-[#2A1F16] leading-snug">{video.title}</h3>
          </div>
          <Youtube size={18} className="text-[#2A1F16]/30 shrink-0 mt-1" />
        </div>
      </div>
    </Reveal>
  );
};

export const Videos = () => (
  <section id="videos" data-testid="section-videos" className="py-24 sm:py-32 bg-[#F4EBDD]">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-14">
        <div>
          <Eyebrow index="08">On YouTube</Eyebrow>
          <h2 className="font-serif-ed font-normal text-3xl sm:text-4xl lg:text-5xl mt-6 text-[#2A1F16]">
            Tutorials &amp; <em className="text-[#96754D]">transformations</em>
          </h2>
        </div>
        <a
          data-testid="youtube-subscribe-button"
          href={YOUTUBE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2.5 text-[11px] uppercase tracking-[0.22em] border border-[#2A1F16]/30 text-[#2A1F16] px-6 py-3.5 rounded-full hover:border-[#B8893B] hover:bg-[#2A1F16] hover:text-[#FBF6EE] transition-colors duration-400"
        >
          <Youtube size={15} />
          Subscribe
          <ArrowUpRight size={13} />
        </a>
      </Reveal>

      <div className="grid md:grid-cols-3 gap-5">
        {YOUTUBE_VIDEOS.map((v, i) => (
          <VideoCard key={v.id} video={v} index={i} />
        ))}
      </div>
    </div>
  </section>
);
